// A sign-up link's URL carries the company name only so the link reads well when it is sent to
// a customer. The token after it identifies the link, so the slug is never checked on the way in.
export function companySlug(name) {
    return name
        .toLowerCase()
        .replace(/æ/g, 'ae')
        .replace(/ø/g, 'oe')
        .replace(/å/g, 'aa')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}

// How an admin sees a link. The redeem function holds the authoritative check; this only
// decides what the Setup list and home-page panel show. Dates are plain Dates.
export function signupLinkDisplayState(link, now) {
    if (link.revoked) return 'revoked';
    if (now >= link.end_date) return 'expired';
    if (now < link.start_date) return 'scheduled';
    if (link.users_signed_up.length >= link.max_users) return 'full';
    return 'active';
}

// A link's start and end are exact times in Copenhagen, whatever the admin's own time zone.
// These convert between a `datetime-local` input value ("2026-10-12T08:00") read as
// Copenhagen wall-clock time and the instant it stands for.
const TIME_ZONE = 'Europe/Copenhagen';

const copenhagenParts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TIME_ZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
});

function copenhagenWallClock(date) {
    return Object.fromEntries(
        copenhagenParts.formatToParts(date).map(({ type, value }) => [type, Number(value)]),
    );
}

// How far Copenhagen's wall clock is ahead of UTC at a given instant, in milliseconds.
function copenhagenOffsetAt(time) {
    const { year, month, day, hour, minute, second } = copenhagenWallClock(new Date(time));
    return Date.UTC(year, month - 1, day, hour, minute, second) - Math.floor(time / 1000) * 1000;
}

export function copenhagenInputToDate(value) {
    const [datePart, timePart] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    const wallClockAsUtc = Date.UTC(year, month - 1, day, hour, minute);

    // The offset depends on the instant, which depends on the offset; a second pass settles
    // it for times next to a daylight-saving change.
    const firstGuess = wallClockAsUtc - copenhagenOffsetAt(wallClockAsUtc);
    return new Date(wallClockAsUtc - copenhagenOffsetAt(firstGuess));
}

export function dateToCopenhagenInput(date) {
    const { year, month, day, hour, minute } = copenhagenWallClock(date);
    const pad = (number) => String(number).padStart(2, '0');
    return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`;
}

export const formatCopenhagenDateTime = (date) =>
    date.toLocaleString('da-DK', {
        timeZone: TIME_ZONE,
        dateStyle: 'short',
        timeStyle: 'short',
    });
